import {Dialog} from '../../ui/overlays/dialog/dialog';
import {
  BackendFilter,
  CustomFilterControl,
  DatePickerFilterControl,
  FilterBooleanToggleControl,
  FilterChipFieldControl,
  FilterControlType,
  FilterOperator,
  FilterSelectControl,
  FilterSelectModelControl,
  FilterTextInputControl,
} from './backend-filter';
import {Trans} from '../../i18n/trans';
import {useEffect, useRef, useState} from 'react';
import {DialogHeader} from '../../ui/overlays/dialog/dialog-header';
import {DialogBody} from '../../ui/overlays/dialog/dialog-body';
import {useBackendFilterUrlParams} from './backend-filter-url-params';
import {useDialogContext} from '../../ui/overlays/dialog/dialog-context';
import {Button} from '../../ui/buttons/button';
import {useForm} from 'react-hook-form';
import {Form} from '../../ui/forms/form';
import {Checkbox} from '../../ui/forms/toggle/checkbox';
import {SelectFilterPanel} from './panels/select-filter-panel';
import {DateRangeFilterPanel} from './panels/date-range-filter-panel';
import {NormalizedModelFilterPanel} from './panels/normalized-model-filter-panel';
import {InputFilterPanel} from './panels/input-filter-panel';
import {BooleanFilterPanel} from './panels/boolean-filter-panel';
import {ChipFieldFilterPanel} from '@common/datatable/filters/panels/chip-field-filter-panel';
import {FilterListValue} from './utils/encode-backend-filters';

export interface FilterItemFormValue<T = any> {
  value: T;
  operator?: FilterOperator;
}

interface AddFilterDialogProps {
  filters: BackendFilter[];
}
export function AddFilterDialog({filters}: AddFilterDialogProps) {
  const {decodedFilters, replaceAll} = useBackendFilterUrlParams(filters);

  // active filters (checked in the list)
  const [expandedFilters, setExpandedFilters] = useState<(string | number)[]>(
    () => {
      return decodedFilters.map(f => f.key);
    },
  );

  const clearButton = (
    <Button
      size="xs"
      variant="outline"
      className="mr-auto"
      onClick={() => {
        setExpandedFilters([]);
        replaceAll([]);
      }}
    >
      <Trans message="Clear" />
    </Button>
  );

  return (
    <Dialog className="min-w-[300px]" maxWidth="max-w-400" size="auto">
      <DialogHeader padding="px-14 py-10" leftAdornment={clearButton}>
        <Trans message="Filter" />
      </DialogHeader>
      <DialogBody padding="p-0">
        <FilterList
          filters={filters}
          expandedFilters={expandedFilters}
          setExpandedFilters={setExpandedFilters}
        />
      </DialogBody>
    </Dialog>
  );
}

interface FilterListProps {
  filters: BackendFilter[];
  expandedFilters: (string | number)[];
  setExpandedFilters: (value: (string | number)[]) => void;
}
function FilterList({
  filters,
  expandedFilters,
  setExpandedFilters,
}: FilterListProps) {
  const {decodedFilters, replaceAll} = useBackendFilterUrlParams(filters);
  const {formId} = useDialogContext();
  const applyTimer = useRef<number | null>(null);
  const expandedFiltersRef = useRef(expandedFilters);
  expandedFiltersRef.current = expandedFilters;

  // either get value and operator from url params if filter is active, or get defaults from filter config
  const defaultValues: Record<string, FilterItemFormValue> = {};
  filters.forEach(filter => {
    const appliedFilter = decodedFilters.find(f => f.key === filter.key);
    defaultValues[filter.key] =
      appliedFilter?.value !== undefined
        ? // there might be some extra keys set on filter besides
          // "value" and "operator", so add the whole object to form
          appliedFilter
        : {
            value: filter.control.defaultValue,
            operator: filter.defaultOperator,
          };
  });
  const form = useForm<Record<string, FilterItemFormValue>>({defaultValues});

  const buildActiveFilters = (
    activeKeys: (string | number)[],
    formValue: Record<string, FilterItemFormValue>,
  ): FilterListValue[] => {
    return Object.entries(formValue)
      .filter(
        ([key, fieldValue]) =>
          activeKeys.includes(key) && fieldValue !== undefined,
      )
      .map(([key, fieldValue]) => ({
        key,
        ...fieldValue,
      }));
  };

  const applyNow = (
    activeKeys: (string | number)[],
    formValue: Record<string, FilterItemFormValue>,
  ) => {
    replaceAll(buildActiveFilters(activeKeys, formValue));
  };

  const applyDebounced = (
    activeKeys: (string | number)[],
    formValue: Record<string, FilterItemFormValue>,
  ) => {
    if (applyTimer.current) {
      window.clearTimeout(applyTimer.current);
    }
    applyTimer.current = window.setTimeout(() => {
      applyNow(activeKeys, formValue);
    }, 250);
  };

  useEffect(() => {
    return () => {
      if (applyTimer.current) {
        window.clearTimeout(applyTimer.current);
      }
    };
  }, []);

  // Apply as soon as field values change (tag input, selects, etc.)
  useEffect(() => {
    const subscription = form.watch(formValue => {
      applyDebounced(
        expandedFiltersRef.current,
        formValue as Record<string, FilterItemFormValue>,
      );
    });
    return () => subscription.unsubscribe();
    // form.watch is stable enough; replaceAll captured via applyDebounced closure on each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, filters]);

  const toggleFilter = (key: string | number) => {
    const filter = filters.find(f => f.key === key);
    let next: (string | number)[];
    if (expandedFilters.includes(key)) {
      next = expandedFilters.filter(k => k !== key);
    } else {
      const blocked = new Set(filter?.exclusiveWith ?? []);
      next = [
        ...expandedFilters.filter(k => k !== key && !blocked.has(String(k))),
        key,
      ];
    }
    setExpandedFilters(next);
    if (applyTimer.current) {
      window.clearTimeout(applyTimer.current);
      applyTimer.current = null;
    }
    applyNow(next, form.getValues());
  };

  return (
    <Form
      form={form}
      id={formId}
      onSubmit={() => {
        // Live-apply only; form submit is a no-op.
      }}
    >
      <div role="presentation">
        {filters.map(filter => {
          const isActive = expandedFilters.includes(filter.key);
          const needsPanel =
            filter.control.type !== FilterControlType.BooleanToggle;
          return (
            <div key={filter.key} className="border-b">
              <label className="flex cursor-pointer items-center gap-10 px-14 py-10 text-sm hover:bg-hover">
                <Checkbox
                  checked={isActive}
                  onChange={() => toggleFilter(filter.key)}
                />
                <span className="flex-auto">
                  <Trans {...filter.label} />
                </span>
              </label>
              {isActive && needsPanel ? (
                <div className="compact-scrollbar max-h-288 overflow-y-auto px-14 pb-14">
                  {filter.description && (
                    <div className="mb-14 text-xs text-muted">
                      <Trans {...filter.description} />
                    </div>
                  )}
                  <AddFilterDialogPanel filter={filter} />
                </div>
              ) : null}
              {isActive && !needsPanel && filter.description ? (
                <div className="px-14 pb-10 text-xs text-muted">
                  <Trans {...filter.description} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Form>
  );
}

interface ActiveFilterPanelProps {
  filter: BackendFilter;
}
export function AddFilterDialogPanel({filter}: ActiveFilterPanelProps) {
  switch (filter.control.type) {
    case FilterControlType.Select:
      return (
        <SelectFilterPanel
          filter={filter as BackendFilter<FilterSelectControl>}
        />
      );
    case FilterControlType.ChipField:
      return (
        <ChipFieldFilterPanel
          filter={filter as BackendFilter<FilterChipFieldControl>}
        />
      );
    case FilterControlType.DateRangePicker:
      return (
        <DateRangeFilterPanel
          filter={filter as BackendFilter<DatePickerFilterControl>}
        />
      );
    case FilterControlType.SelectModel:
      return (
        <NormalizedModelFilterPanel
          filter={filter as BackendFilter<FilterSelectModelControl>}
        />
      );
    case FilterControlType.Input:
      return (
        <InputFilterPanel
          filter={filter as BackendFilter<FilterTextInputControl>}
        />
      );
    case FilterControlType.BooleanToggle:
      return (
        <BooleanFilterPanel
          filter={filter as BackendFilter<FilterBooleanToggleControl>}
        />
      );
    case 'custom':
      const CustomComponent = filter.control.panel;
      return (
        <CustomComponent
          filter={filter as BackendFilter<CustomFilterControl>}
        />
      );
    default:
      return null;
  }
}
